/**
 * O caminho de cada tela, e a porta na frente de todas.
 *
 * `HashRouter` de propósito: o mesmo pacote vai rodar no site, no Electron e
 * em qualquer lugar onde o servidor não reescreve URL. Um `BrowserRouter`
 * exigiria essa reescrita e quebraria em recarga fora do Caddy.
 */

import { Suspense, useEffect, useState } from 'react';
import { HashRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {
  Idiomas,
  ProvedorDeSessao,
  temaDe,
  traduzir,
  usarAjustes,
  usarSessao,
} from '@nosso-sempre/shared';
// Só pelo efeito: monta o cliente, o armazém e o `ligar()` que o núcleo
// compartilhado espera. Fica aqui e não no `main.tsx` para que qualquer lugar
// que monte `<App/>` já encontre a ligação pronta.
import './nucleo/api';
// As cinco que aparecem antes de qualquer toque vêm no primeiro pedaço; as
// outras trinta e oito chegam por `import()` quando a rota abre. Ver o
// cabeçalho de `telas/tardias.ts` para o porquê da divisão.
import { Moldura } from './componentes/Moldura';
import { Entrada } from './telas/Entrada';
import { Comecar } from './telas/Comecar';
import { Painel } from './telas/Painel';
import { Kovu } from './telas/Kovu';
import { TarjaDeSessao } from './telas/cinema/tarja';
import { TocadorDeMusica } from './nucleo/tocador';
import { BarraDoTocador } from './componentes/BarraDoTocador';
import { Barreira } from './componentes/Barreira';
import {
  Academia,
  Ajustes,
  Alexa,
  Anime,
  Baixar,
  Caderno,
  Calendario,
  Calendarios,
  Cardapio,
  Cartas,
  Cinema,
  Compras,
  Desejos,
  Despensa,
  Detalhe,
  Dupla,
  Estudo,
  Financas,
  Galeria,
  Jardim,
  Jogos,
  KovuHoje,
  Leitor,
  Lembrancas,
  Livros,
  NossosLivros,
  Ouvir,
  PingPong,
  Podio,
  Quadro,
  Quiz,
  Receita,
  Receitas,
  Refugio,
  Sala,
  Sessao,
  Tabuleiro,
  Tocador,
  aquecerQuandoSobrar,
} from './telas/tardias';
import { aplicarPaleta } from './nucleo/paleta';
import { ouvirCliques } from './nucleo/avisos';

export function App() {
  return (
    /* Por fora de tudo, inclusive da sessão: a porta de senha também fala a
       língua da pessoa, e ela desenha antes de qualquer resposta do servidor. */
    <Idiomas>
      <ProvedorDeSessao>
        <HashRouter>
          <Portao />
        </HashRouter>
      </ProvedorDeSessao>
    </Idiomas>
  );
}

/**
 * As duas coisas que valem para o app inteiro: a cor da casa e o clique no
 * aviso. Não desenha nada — mora aqui, dentro do roteador e da sessão, porque
 * as duas precisam de `useNavigate` e de saber quem entrou.
 */
function Ambiente() {
  const navegar = useNavigate();
  const { quem } = usarSessao();
  const ajustes = usarAjustes();
  const tema = temaDe(ajustes, quem ?? undefined);

  // Lá o `AppSettings._apply` chama `AppPalette.escolher(themeOf(_slug))` a
  // cada carga de ajustes: a cópia do servidor manda. A diferença é que aqui
  // só se pinta quando o servidor de fato disse uma cor — enquanto os ajustes
  // não chegam vale o que o `restaurarPaleta()` leu do navegador, senão o app
  // abriria rosa e trocaria de cor meio segundo depois, toda vez.
  useEffect(() => {
    if (tema !== '') aplicarPaleta(tema);
  }, [tema]);

  // O worker manda a rota do aviso clicado; quem navega é o roteador.
  useEffect(() => ouvirCliques((rota) => navegar(rota)), [navegar]);

  // As outras telas chegam em segundo plano enquanto a pessoa olha o painel.
  // Aqui e não no `App`, de propósito: este componente só monta depois que a
  // senha confere, então a porta pinta com o pedaço pequeno e ninguém gasta
  // rede baixando a casa inteira para quem não entrou.
  useEffect(() => aquecerQuandoSobrar(), []);

  return null;
}

/**
 * O que fica no lugar da tela enquanto o pedaço dela não chegou.
 *
 * Na prática quase nunca aparece: o `aquecer()` já trouxe tudo antes do
 * primeiro toque. Sobra para a primeira abertura numa rede ruim e para quem
 * pediu economia de dados — e aí vale a mesma cara e as mesmas palavras da
 * conferência de sessão, que é o jeito que este app já diz "espera".
 */
function Carregando() {
  return (
    <main className="conferindo" aria-busy="true">
      <p className="discreto">{traduzir('Abrindo...')}</p>
    </main>
  );
}

function Portao() {
  const { estado } = usarSessao();
  // A sessão nasce no instante em que a senha confere, mas a porta ainda tem
  // de girar. Enquanto ela avisa que está abrindo, a casa espera atrás.
  const [abrindo, setAbrindo] = useState(false);

  // Enquanto o cookie não foi conferido não dá para escolher entre a porta e a
  // casa; mostrar a porta aqui faria a senha ser pedida a cada abertura.
  if (estado === 'verificando') {
    return (
      <main className="conferindo" aria-busy="true">
        <p className="discreto">{traduzir('Abrindo...')}</p>
      </main>
    );
  }

  // Fora da casa só existem dois endereços: a porta de senha e a porta da rua.
  // Qualquer outro cai na porta, que é o que o `redirect` do `app_router` faz
  // com quem ainda não entrou.
  if (estado === 'fora' || abrindo) {
    return (
      <Routes>
        <Route path="/comecar" element={<Comecar />} />
        <Route path="*" element={<Entrada aoAbrir={setAbrindo} />} />
      </Routes>
    );
  }

  return (
    /* O tocador envolve tudo porque o `<audio>` dele não pode morrer quando a
       tela troca -- e trocar de tela é o que se faz o tempo todo. Fica dentro
       da porta de senha: sair da casa cala a música. */
    <TocadorDeMusica>
      <Ambiente />
      {/* Fora das rotas de propósito: a sessão de cinema que a outra pessoa
          abriu tem de se anunciar esteja quem está aqui em que tela estiver. */}
      <TarjaDeSessao />
      {/* A barreira por fora do `Suspense`, e não por dentro: assim ela apanha
          duas quedas em vez de uma -- a tela que quebra no meio do desenho e o
          pedaço que nunca chega, porque um `import()` recusado é atirado como
          erro na volta seguinte. Ver `componentes/Barreira.tsx` para por que
          ela para aqui e não envolve o tocador. */}
      <Barreira>
        {/* Uma fronteira só, em volta de todas as rotas, e não uma por tela: o
            que se troca aqui é sempre a tela inteira, então não há nada de
            parcial para preservar enquanto o pedaço vem. */}
        <Suspense fallback={<Carregando />}>
          <Routes>
            <Route element={<Moldura />}>
              <Route index element={<Painel />} />
              <Route path="kovu" element={<Kovu />} />
              {/* As três telas que saem do Kovu. Ficam dentro da moldura, como no
                `app_router.dart`, e nenhuma delas passa por ajuste de recurso:
                quem alcança o Kovu alcança o caderno dele. */}
              <Route path="kovu-today" element={<KovuHoje />} />
              <Route path="kovu-trivia" element={<Quiz />} />
              <Route path="kovu-memory" element={<Caderno />} />
              <Route path="lembrancas" element={<Lembrancas />} />
              <Route path="desejos" element={<Desejos />} />
              {/* A cozinha: o livro, a semana, o mercado e o que tem em casa. Não
                entra na barra de baixo — cinco destinos a mais quebrariam a
                navegação — então chega pelos atalhos do painel. */}
              <Route path="receitas" element={<Receitas />} />
              <Route path="receitas/:id" element={<Receita />} />
              <Route path="cardapio" element={<Cardapio />} />
              <Route path="compras" element={<Compras />} />
              <Route path="despensa" element={<Despensa />} />
              <Route path="calendario" element={<Calendario />} />
              {/* Fica dentro da moldura, como lá: o `app_router.dart` não gasta
                rota nenhuma com esta tela nos ajustes de recurso, e o menu de
                baixo continua na frente enquanto se mexe nos calendários. */}
              <Route path="calendars" element={<Calendarios />} />
              <Route path="galeria" element={<Galeria />} />
              <Route path="alexa" element={<Alexa />} />
              {/* A conta no banco é de quem é titular dela. O servidor já recusa,
                mas recusar aqui também evita mandar a outra pessoa para uma tela
                de erro — e, pior, derrubar a sessão dela num 401. */}
              <Route path="financas" element={<Financas />} />
              {/* `/letterbox`: o arquivo de cartas. Fica dentro da moldura porque
                é uma lista como as outras, e a barra de baixo não atrapalha. */}
              <Route path="letterbox" element={<Cartas />} />
              <Route path="ajustes" element={<Ajustes />} />
            </Route>
            {/* O cinema é tela cheia, com barra de abas própria: fica fora da
              moldura para não ganhar a barra de baixo da casa por cima da dele. */}
            <Route path="cinema" element={<Cinema />} />
            {/* A sessão de assistir junto é a mesma ideia levada ao limite: nada de
              moldura, porque o vídeo ocupa tudo o que sobra da tela. */}
            <Route path="cinema/sala" element={<Sessao />} />
            {/* `cinema_routes.dart`: `video/:id`. Abre sozinho por link direto, sem
              passar pela estante — por isso lê tudo o que precisa do endereço. */}
            <Route path="cinema/video/:id" element={<Tocador />} />
            {/* O caminho é o mesmo do celular (`/party-downloads`) porque a sessão
              manda para cá pelo nome, e um nome só serve se for igual dos dois
              lados. */}
            <Route path="party-downloads" element={<Baixar />} />
            {/* A biblioteca segue o cinema: cinco abas e barra própria embaixo, e
              por isso também fica fora da moldura. */}
            <Route path="books" element={<Livros />} />
            {/* O livro por dentro. Chega com o livro no `state`, como o
              `context.push('/books/detail', extra: …)` de lá. */}
            <Route path="books/detail" element={<Detalhe />} />
            {/* Ler e ouvir são a mesma tela, como no Flutter: o `ReaderScreen`
              tem um `startListening`, e não uma tela irmã. */}
            <Route path="books/reader" element={<Leitor />} />
            <Route path="books/listen" element={<Leitor ouvindo />} />
            {/* O audiolivro tem tocador próprio, e chega com o audiolivro inteiro
              no `state` — a lista de faixas não cabe num endereço. */}
            <Route path="books/audiobook" element={<Ouvir />} />
            {/* `/our-books` fica ao lado de `/books`, e não dentro: é tela cheia
              com cabeçalho próprio, do mesmo jeito que a estante. */}
            <Route path="our-books" element={<NossosLivros />} />
            {/* O Refúgio tem cabeçalho e barra de escrever próprios, os dois
              colados nas bordas: fica fora da moldura pelo mesmo motivo do
              cinema. */}
            <Route path="sanctuary" element={<Refugio />} />
            {/* O quadro é a folha inteira: a barra de baixo da casa cobriria
              justamente o canto onde ficam a caneta e as cores. Fora da
              moldura, então. */}
            <Route path="canvas" element={<Quadro />} />
            {/* O jardim também: o `garden_routes.dart` registra `/garden` na raiz,
              e não dentro da `ShellRoute`. A tela traz o próprio botão de
              voltar. */}
            <Route path="garden" element={<Jardim />} />
            {/* A academia idem: o `academy_routes.dart` põe `/academy` na raiz com
              os três filhos. As telas de dentro recebem as perguntas pelo estado
              da navegação, que é o `extra` do `go_router` — quem recarregar a aba
              em uma delas volta para a central em vez de quebrar. */}
            <Route path="academy" element={<Academia />} />
            <Route path="academy/solo" element={<Estudo />} />
            <Route path="academy/match" element={<Tabuleiro />} />
            <Route path="academy/podium" element={<Podio />} />
            {/* A Zona de Jogos também está na raiz no `play_zone_routes.dart`. As
              duas telas de jogo são o `iframe` inteiro, sem margem para a barra
              da casa; a partida 1v1 recebe a sala pelo estado da navegação, como
              as telas de dentro da academia. */}
            <Route path="play-zone" element={<Jogos />} />
            <Route path="play-zone/tt" element={<PingPong />} />
            <Route path="play-zone/tt/lobby" element={<Sala />} />
            <Route path="play-zone/tt/match" element={<Dupla />} />
            {/* `cinema_routes.dart` registra `/anime` na raiz, e não dentro do
              cinema: a seção tem barra de cima e de baixo próprias, e ganharia
              duas barras se entrasse na moldura. Qual tela da seção está na
              frente vai no `?p=`, para o voltar do navegador andar dentro dela. */}
            <Route path="anime" element={<Anime />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Barreira>
      {/* Fora das rotas pelo mesmo motivo da tarja, e fora do `Suspense` por
          mais um: metade das telas -- cinema, livros, jardim, os jogos -- não
          entra na moldura, e a barra morava lá dentro. A música seguia tocando
          nelas sem nada na tela para pausá-la. Aqui ela alcança toda tela de
          dentro da casa, e um pedaço de tela a caminho não a desliga. Fora da
          barreira pela mesma razão: uma tela que cai não pode deixar a música
          tocando sem nada para pará-la. */}
      <BarraDoTocador />
    </TocadorDeMusica>
  );
}
